import { ValidateRecaptcha } from 'utility/Validations'

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

const RateRequest = {
  init() {
    const form = document.querySelector<HTMLFormElement>('[data-rate-request-form]')

    if (!form) {
      return
    }

    const steps = Array.from(form.querySelectorAll<HTMLElement>('[data-rate-request-step]'))
    const stepItems = Array.from(form.querySelectorAll<HTMLElement>('[data-rate-request-step-item]'))
    const backButton = form.querySelector<HTMLButtonElement>('[data-rate-request-back]')
    const nextButton = form.querySelector<HTMLButtonElement>('[data-rate-request-next]')
    const submitButton = form.querySelector<HTMLButtonElement>('[data-rate-request-submit]')
    const recaptcha = form.querySelector<HTMLElement>('[data-rate-request-recaptcha]')
    const recaptchaError = recaptcha?.querySelector<HTMLElement>('.form__recaptcha-error')
    const upload = form.querySelector<HTMLElement>('[data-rate-request-upload]')
    const fileInput = form.querySelector<HTMLInputElement>('[data-rate-request-file]')
    const fileName = form.querySelector<HTMLElement>('[data-rate-request-file-name]')
    const fileError = form.querySelector<HTMLElement>('[data-rate-request-file-error]')
    const fileHint = fileName?.textContent ?? ''
    const routeArrow = form.querySelector<SVGElement>('[data-rate-request-route-arrow] svg')
    let current = 1
    let reached = 1

    const getFields = (step: HTMLElement) =>
      Array.from(step.querySelectorAll<Field>('input, select, textarea'))

    const isStepValid = (step: HTMLElement) => getFields(step).every((field) => field.checkValidity())

    const getFieldText = (field: Field) =>
      field instanceof HTMLSelectElement ? field.selectedOptions[0]?.textContent?.trim() ?? '' : field.value.trim()

    const renderSummary = (index: number) => {
      const summary = form.querySelector<HTMLElement>(`[data-rate-request-summary="${index}"]`)
      const step = steps[index - 1]

      if (!summary || !step) {
        return
      }

      summary.replaceChildren()

      if (index >= reached || index === current) {
        return
      }

      const routes = Array.from(step.querySelectorAll<HTMLSelectElement>('[data-rate-request-route]'))

      if (routes.length > 0 && routes.every((route) => route.value)) {
        const line = document.createElement('p')
        line.className = 'rate-request__route'

        routes.forEach((route, i) => {
          if (i > 0 && routeArrow) {
            line.append(routeArrow.cloneNode(true))
          }

          const text = document.createElement('span')
          text.textContent = getFieldText(route)
          line.append(text)
        })

        summary.append(line)
      }

      step.querySelectorAll<Field>('[data-rate-request-summary-field]').forEach((field) => {
        const value = getFieldText(field)

        if (!value) {
          return
        }

        const line = document.createElement('p')
        line.textContent = (field.dataset.rateRequestSummaryField ?? '{0}').replace('{0}', value)
        summary.append(line)
      })
    }

    const update = () => {
      const step = steps[current - 1]
      const isLast = current === steps.length

      steps.forEach((item, i) => {
        item.hidden = i !== current - 1
      })

      stepItems.forEach((item, i) => {
        const index = i + 1
        const button = item.querySelector<HTMLButtonElement>('[data-rate-request-goto]')

        item.classList.toggle('rate-request__step--active', index === current)
        item.classList.toggle('rate-request__step--done', index < reached && index !== current)

        if (button) {
          button.disabled = index > reached || index === current
        }

        renderSummary(index)
      })

      if (backButton) {
        backButton.disabled = current === 1
      }

      if (nextButton) {
        nextButton.hidden = isLast
        nextButton.disabled = !isStepValid(step)
      }

      if (submitButton) {
        submitButton.hidden = !isLast
        submitButton.disabled = !isStepValid(step)
      }

      if (recaptcha) {
        recaptcha.hidden = !isLast
      }
    }

    const goTo = (index: number) => {
      current = index
      update()
    }

    const setFile = (file: File | undefined) => {
      const maxSize = Number(fileInput?.dataset.maxSize ?? 0)
      const isTooLarge = Boolean(file && maxSize && file.size > maxSize)

      if (fileInput && isTooLarge) {
        fileInput.value = ''
      }

      if (fileError) {
        fileError.hidden = !isTooLarge
      }

      if (fileName) {
        fileName.textContent = file && !isTooLarge ? file.name : fileHint
      }
    }

    nextButton?.addEventListener('click', () => {
      if (!isStepValid(steps[current - 1])) {
        return
      }

      reached = Math.max(reached, current + 1)
      goTo(current + 1)
    })

    backButton?.addEventListener('click', () => {
      if (current > 1) {
        goTo(current - 1)
      }
    })

    stepItems.forEach((item) => {
      item.querySelector<HTMLButtonElement>('[data-rate-request-goto]')?.addEventListener('click', (event) => {
        const index = Number((event.currentTarget as HTMLButtonElement).dataset.rateRequestGoto)

        if (index <= reached) {
          goTo(index)
        }
      })
    })

    form.addEventListener('input', update)
    form.addEventListener('change', update)

    form.addEventListener('submit', (event) => {
      if (!form.checkValidity()) {
        event.preventDefault()
        return
      }

      if (recaptchaError && !ValidateRecaptcha()) {
        event.preventDefault()
        recaptchaError.classList.remove('hide')
        return
      }

      if (submitButton) {
        submitButton.disabled = true
      }
    })

    fileInput?.addEventListener('change', () => {
      setFile(fileInput.files?.[0])
    })

    upload?.addEventListener('dragover', (event) => {
      event.preventDefault()
      upload.classList.add('rate-request__upload--dragover')
    })

    upload?.addEventListener('dragleave', () => {
      upload.classList.remove('rate-request__upload--dragover')
    })

    upload?.addEventListener('drop', (event) => {
      event.preventDefault()
      upload.classList.remove('rate-request__upload--dragover')

      if (!fileInput || !event.dataTransfer?.files.length) {
        return
      }

      fileInput.files = event.dataTransfer.files
      setFile(fileInput.files[0])
    })

    update()
  },
}

export default RateRequest
